import React, { useContext, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  BadgePercent,
  Bell,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  CreditCard,
  Download,
  FileText,
  Fingerprint,
  HandCoins,
  IdCard,
  KeyRound,
  Landmark,
  Laptop,
  LayoutDashboard,
  Lock,
  LogOut,
  Menu,
  PiggyBank,
  Receipt,
  Search,
  ShieldAlert,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Target,
  TrendingUp,
  UserRound,
  Vote,
  Wallet,
  X,
} from 'lucide-react';
import { AuthContext } from '../context/AuthContext';

const FONT_SCALE = 0.85;
const fs = (px) => `${Math.round(px * FONT_SCALE * 100) / 100}px`;

const formatCurrency = (value) =>
  `₦${Number(value || 0).toLocaleString('en-NG', {
    maximumFractionDigits: 0,
  })}`;

const sectionIcons = {
  overview: LayoutDashboard,
  wallet: Wallet,
  transactions: Receipt,
  loans: HandCoins,
  dividends: BadgePercent,
  investments: BriefcaseBusiness,
  profile: UserRound,
  security: ShieldCheck,
};

const MemberDashboard = () => {
  const { user, logout } = useContext(AuthContext);

  const [balance, setBalance] = useState(9800);
  const [transactions, setTransactions] = useState([
    {
      id: 1,
      date: '01-Oct-2025',
      description: 'Monthly Contribution (Payroll)',
      debit: 10000,
      credit: 0,
      category: 'contribution',
      status: 'Successful',
    },
    {
      id: 2,
      date: '01-Oct-2025',
      description: 'Service Fee (Developer)',
      debit: 200,
      credit: 0,
      category: 'fee',
      status: 'Successful',
    },
    {
      id: 3,
      date: '01-Oct-2025',
      description: 'Cooperative Wallet Credit',
      debit: 0,
      credit: 9800,
      category: 'wallet',
      status: 'Successful',
    },
    {
      id: 4,
      date: '15-Oct-2025',
      description: 'Loan Repayment (Month 3 of 12)',
      debit: 1000,
      credit: 0,
      category: 'loan',
      status: 'Successful',
    },
    {
      id: 5,
      date: '31-Dec-2025',
      description: 'Dividend Payout',
      debit: 0,
      credit: 5000,
      category: 'dividend',
      status: 'Successful',
    },
  ]);

  const [loanStatus] = useState({
    amount: 10000,
    remaining: 7000,
    nextDue: '15-Apr-2026',
    type: 'Personal Loan',
    tenure: '12 Months',
    eligibility: 'Eligible for up to ₦150,000',
    score: 'Good standing',
  });

  const [dividend] = useState({
    year: 2025,
    amount: 45000,
    reinvestable: 30000,
    status: 'Ready for reinvestment',
    lastPaid: '31-Dec-2025',
  });

  const [newDeposit, setNewDeposit] = useState('');
  const [newWithdrawal, setNewWithdrawal] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeAction, setActiveAction] = useState('deposit');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const monthlyContribution = 10000;
  const totalSaved = 184500;
  const sharesOwned = 125;
  const pendingApproval = 2;
  const savingsGoal = 300000;

  const announcements = [
    {
      title: 'Quarterly AGM Voting Opens',
      desc: 'Cast your vote for executive positions before Friday, 6:00 PM.',
      icon: Vote,
    },
    {
      title: 'Real Estate Investment Window',
      desc: 'New cooperative housing project now open for share subscription.',
      icon: Building2,
    },
    {
      title: 'Loan Committee Review Cycle',
      desc: 'Submitted loan applications will be reviewed every Wednesday.',
      icon: Clock3,
    },
  ];

  const investmentOptions = [
    {
      title: 'FAAN Housing Scheme',
      roi: '12% Annual Return',
      minimum: 'Min: ₦50,000',
      tag: 'High Demand',
      status: 'Subscription Open',
      units: '48 units left',
    },
    {
      title: 'Retail Supply Fund',
      roi: '9% Annual Return',
      minimum: 'Min: ₦20,000',
      tag: 'Low Risk',
      status: 'Stable',
      units: 'Rolling plan',
    },
    {
      title: 'Transport Asset Pool',
      roi: '14% Annual Return',
      minimum: 'Min: ₦100,000',
      tag: 'Premium',
      status: 'Featured',
      units: 'Limited slots',
    },
  ];

  const dividendHistory = [
    { year: 2025, amount: 45000, status: 'Paid', date: '31-Dec-2025' },
    { year: 2024, amount: 32000, status: 'Paid', date: '31-Dec-2024' },
    { year: 2023, amount: 27000, status: 'Paid', date: '31-Dec-2023' },
  ];

  const walletAccounts = [
    {
      title: 'Cooperative Wallet',
      balance: balance,
      description: 'Main wallet for deposits and service payments',
      tag: 'Primary',
    },
    {
      title: 'Share Capital Wallet',
      balance: 125000,
      description: 'Tracks equity contributions and share subscriptions',
      tag: 'Investments',
    },
    {
      title: 'Dividend Wallet',
      balance: dividend.amount,
      description: 'Holds unpaid and reinvestable dividends',
      tag: 'Returns',
    },
  ];

  const loanProducts = [
    {
      title: 'Personal Loan',
      limit: 'Up to ₦150,000',
      tenor: '3 - 12 months',
      rate: '10% flat',
    },
    {
      title: 'Emergency Loan',
      limit: 'Up to ₦75,000',
      tenor: '1 - 6 months',
      rate: '8% flat',
    },
    {
      title: 'Business Support Loan',
      limit: 'Up to ₦500,000',
      tenor: '6 - 18 months',
      rate: '12% flat',
    },
  ];

  const repaymentSchedule = [
    { label: 'Month 4', due: '15-Apr-2026', amount: 1000, status: 'Upcoming' },
    { label: 'Month 5', due: '15-May-2026', amount: 1000, status: 'Scheduled' },
    { label: 'Month 6', due: '15-Jun-2026', amount: 1000, status: 'Scheduled' },
  ];

  const portfolioSummary = [
    { title: 'Total Invested', value: formatCurrency(225000), icon: TrendingUp },
    { title: 'Projected Returns', value: formatCurrency(34800), icon: CircleDollarSign },
    { title: 'Active Products', value: '3 Active', icon: BriefcaseBusiness },
  ];

  const profileInfo = {
    fullName: user?.name || 'Member User',
    memberId: 'FAAN-CS-1024',
    staffId: 'FAAN-STF-8891',
    department: 'Operations',
    phone: '+234 803 111 2233',
    email: 'member@faancoop.demo',
    bank: 'Zenith Bank •••• 4102',
    nextOfKin: 'Amina Ibrahim',
    nextOfKinPhone: '+234 808 555 0101',
    address: 'Abuja, Nigeria',
    kyc: 'Verified',
  };

  const securitySettings = [
    {
      title: 'Two-Factor Authentication',
      desc: 'Extra login protection using OTP verification.',
      status: 'Enabled',
      icon: Fingerprint,
    },
    {
      title: 'Device Trust',
      desc: 'This device is recognized as a trusted member device.',
      status: 'Trusted',
      icon: Smartphone,
    },
    {
      title: 'Password Strength',
      desc: 'Your account password meets the recommended security policy.',
      status: 'Strong',
      icon: Lock,
    },
  ];

  const loginActivity = [
    {
      device: 'Chrome on Windows',
      time: 'Today, 10:42 AM',
      location: 'Abuja, Nigeria',
      status: 'Current Session',
      icon: Laptop,
    },
    {
      device: 'Mobile App Session',
      time: 'Yesterday, 08:14 PM',
      location: 'Kaduna, Nigeria',
      status: 'Verified',
      icon: Smartphone,
    },
    {
      device: 'Security Password Reset',
      time: '03-Mar-2026',
      location: 'Web Portal',
      status: 'Completed',
      icon: KeyRound,
    },
  ];

  const quickActions = [
    { id: 'deposit', label: 'Fund Wallet', icon: ArrowDownLeft },
    { id: 'withdraw', label: 'Withdraw', icon: ArrowUpRight },
    { id: 'loan', label: 'Apply Loan', icon: HandCoins },
    { id: 'statement', label: 'Statement', icon: FileText },
    { id: 'invest', label: 'Invest', icon: TrendingUp },
    { id: 'vote', label: 'Vote', icon: Vote },
  ];

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'wallet', label: 'Wallet', icon: Wallet },
    { id: 'transactions', label: 'Transactions', icon: Receipt },
    { id: 'loans', label: 'Loans', icon: HandCoins },
    { id: 'dividends', label: 'Dividends', icon: BadgePercent },
    { id: 'investments', label: 'Investments', icon: BriefcaseBusiness },
    { id: 'profile', label: 'Profile', icon: UserRound },
    { id: 'security', label: 'Security', icon: ShieldCheck },
  ];

  const sectionMeta = {
    overview: {
      title: 'Member Dashboard',
      subtitle:
        'Manage savings, dividends, loans, voting, and cooperative investments in one place.',
      badge: 'All systems active',
    },
    wallet: {
      title: 'Wallet Center',
      subtitle:
        'Track balances, move funds, review wallet channels, and manage cooperative cash flow.',
      badge: 'Wallet ready',
    },
    transactions: {
      title: 'Transactions',
      subtitle:
        'Search, filter, and review all contribution, wallet, fee, and dividend activities.',
      badge: 'Live ledger',
    },
    loans: {
      title: 'Loans Hub',
      subtitle:
        'Review eligibility, repayment schedule, loan products, and application readiness.',
      badge: 'Loan access open',
    },
    dividends: {
      title: 'Dividend Center',
      subtitle:
        'Track annual returns, reinvestment options, payout history, and dividend summaries.',
      badge: 'Returns updated',
    },
    investments: {
      title: 'Investment Marketplace',
      subtitle:
        'Discover cooperative-backed products, share subscriptions, and portfolio projections.',
      badge: '3 live offers',
    },
    profile: {
      title: 'Profile & KYC',
      subtitle:
        'Manage personal details, employment profile, next-of-kin, and linked bank information.',
      badge: 'Profile verified',
    },
    security: {
      title: 'Security Center',
      subtitle:
        'Monitor trusted devices, login activity, password health, and account protection tools.',
      badge: 'Protected',
    },
  };

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      const tablet = window.innerWidth < 1120;
      setIsMobile(mobile);
      setIsTablet(tablet);
      if (!tablet) setSidebarOpen(false);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDeposit = (e) => {
    e.preventDefault();
    const amount = parseFloat(newDeposit);
    if (!amount || amount <= 0) return;

    setBalance((prev) => prev + amount);
    setTransactions((prev) => [
      {
        id: Date.now(),
        date: new Date().toLocaleDateString('en-GB'),
        description: 'Wallet Funding',
        debit: 0,
        credit: amount,
        category: 'wallet',
        status: 'Successful',
      },
      ...prev,
    ]);
    setNewDeposit('');
  };

  const handleWithdraw = (e) => {
    e.preventDefault();
    const amount = parseFloat(newWithdrawal);
    if (!amount || amount <= 0 || amount > balance) return;

    setBalance((prev) => prev - amount);
    setTransactions((prev) => [
      {
        id: Date.now(),
        date: new Date().toLocaleDateString('en-GB'),
        description: 'Wallet Withdrawal',
        debit: amount,
        credit: 0,
        category: 'withdrawal',
        status: 'Processing',
      },
      ...prev,
    ]);
    setNewWithdrawal('');
  };

  const filteredTransactions = useMemo(() => {
    return transactions.filter((item) => {
      const matchesSearch =
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.date.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter =
        filterType === 'all' ? true : item.category === filterType;

      return matchesSearch && matchesFilter;
    });
  }, [transactions, searchTerm, filterType]);

  const creditedTotal = useMemo(
    () => transactions.reduce((sum, item) => sum + (item.credit || 0), 0),
    [transactions]
  );

  const debitedTotal = useMemo(
    () => transactions.reduce((sum, item) => sum + (item.debit || 0), 0),
    [transactions]
  );

  const loanRepaid = loanStatus.amount - loanStatus.remaining;
  const loanProgress = Math.min(
    100,
    Math.round((loanRepaid / loanStatus.amount) * 100)
  );

  const savingsProgress = Math.min(
    100,
    Math.round((totalSaved / savingsGoal) * 100)
  );

  const currentActionConfig = {
    deposit: {
      title: 'Fund Cooperative Wallet',
      description:
        'Add money into your wallet for share purchase, savings top-up, and product subscriptions.',
      accent: '#0f9f61',
    },
    withdraw: {
      title: 'Request Withdrawal',
      description:
        'Withdraw available wallet funds to your registered bank account.',
      accent: '#f59e0b',
    },
    loan: {
      title: 'Loan Access Center',
      description:
        'View eligibility, estimated limits, and prepare a new loan application.',
      accent: '#16a34a',
    },
    statement: {
      title: 'Account Statement',
      description:
        'Download contribution, dividend, and wallet statement instantly.',
      accent: '#2563eb',
    },
    invest: {
      title: 'Investment Marketplace',
      description:
        'Subscribe to cooperative-backed opportunities and track projected returns.',
      accent: '#7c3aed',
    },
    vote: {
      title: 'Elections & Polls',
      description:
        'Vote securely in cooperative elections and member resolutions.',
      accent: '#db2777',
    },
  }[activeAction];

  const currentSection = sectionMeta[activeSection];
  const CurrentSectionIcon = sectionIcons[activeSection];

  const goToSection = (id) => {
    setActiveSection(id);
    if (isTablet) setSidebarOpen(false);
  };

  const renderActionPanel = () => {
    if (activeAction === 'deposit') {
      return (
        <form onSubmit={handleDeposit} style={styles.actionForm}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Amount</label>
            <input
              type="number"
              value={newDeposit}
              onChange={(e) => setNewDeposit(e.target.value)}
              placeholder="Enter amount"
              style={styles.inputField}
            />
          </div>
          <div
            style={{
              ...styles.formGrid,
              ...(isMobile ? styles.formGridMobile : {}),
            }}
          >
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Suggested</span>
              <strong style={styles.miniInfoValue}>₦10,000</strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Wallet Balance</span>
              <strong style={styles.miniInfoValue}>{formatCurrency(balance)}</strong>
            </div>
          </div>
          <button type="submit" style={styles.primaryButton}>
            Confirm Deposit
          </button>
        </form>
      );
    }

    if (activeAction === 'withdraw') {
      return (
        <form onSubmit={handleWithdraw} style={styles.actionForm}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Withdrawal Amount</label>
            <input
              type="number"
              value={newWithdrawal}
              onChange={(e) => setNewWithdrawal(e.target.value)}
              placeholder="Enter amount"
              style={styles.inputField}
            />
          </div>
          <div
            style={{
              ...styles.formGrid,
              ...(isMobile ? styles.formGridMobile : {}),
            }}
          >
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Available</span>
              <strong style={styles.miniInfoValue}>{formatCurrency(balance)}</strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Destination</span>
              <strong style={styles.miniInfoValue}>Primary Bank</strong>
            </div>
          </div>
          <button type="submit" style={styles.primaryButtonAlt}>
            Request Withdrawal
          </button>
        </form>
      );
    }

    if (activeAction === 'loan') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.loanEligibility}>
            <CheckCircle2 size={18} />
            <span>{loanStatus.eligibility}</span>
          </div>
          <div
            style={{
              ...styles.formGrid,
              ...(isMobile ? styles.formGridMobile : {}),
            }}
          >
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Loan Type</span>
              <strong style={styles.miniInfoValue}>{loanStatus.type}</strong>
            </div>
            <div style={styles.miniInfoCard}>
              <span style={styles.miniInfoLabel}>Tenure</span>
              <strong style={styles.miniInfoValue}>{loanStatus.tenure}</strong>
            </div>
          </div>
          <button style={styles.primaryButton}>Start Loan Application</button>
        </div>
      );
    }

    if (activeAction === 'statement') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.statementCard}>
            <Download size={18} />
            <span>Contribution, loan, and dividend statements are ready.</span>
          </div>
          <button style={styles.primaryButton}>Download PDF Statement</button>
        </div>
      );
    }

    if (activeAction === 'invest') {
      return (
        <div style={styles.actionStack}>
          <div style={styles.statementCard}>
            <Sparkles size={18} />
            <span>
              3 active cooperative opportunities currently accepting subscriptions.
            </span>
          </div>
          <button style={styles.primaryButton}>Explore Investments</button>
        </div>
      );
    }

    return (
      <div style={styles.actionStack}>
        <div style={styles.statementCard}>
          <Vote size={18} />
          <span>You have 1 open election and 2 member polls awaiting your vote.</span>
        </div>
        <button style={styles.primaryButton}>Open Voting Center</button>
      </div>
    );
  };

  const renderSectionHero = (stats = []) => (
    <motion.section
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.32 }}
      style={{
        ...styles.pageHero,
        ...(isTablet ? styles.pageHeroTablet : {}),
      }}
    >
      <div style={styles.pageHeroLeft}>
        <div style={styles.pageHeroBadge}>
          <CurrentSectionIcon size={15} />
          {currentSection.badge}
        </div>
        <h1
          style={{
            ...styles.pageHeroTitle,
            ...(isMobile ? styles.pageHeroTitleMobile : {}),
          }}
        >
          {currentSection.title}
        </h1>
        <p style={styles.pageHeroText}>{currentSection.subtitle}</p>
      </div>

      {!!stats.length && (
        <div
          style={{
            ...styles.pageHeroStats,
            ...(isMobile ? styles.pageHeroStatsMobile : {}),
          }}
        >
          {stats.map((item) => (
            <div key={item.label} style={styles.pageHeroStatCard}>
              <span style={styles.pageHeroStatLabel}>{item.label}</span>
              <strong style={styles.pageHeroStatValue}>{item.value}</strong>
            </div>
          ))}
        </div>
      )}
    </motion.section>
  );

  const renderTransactionList = (items) => {
    if (isMobile) {
      return (
        <div style={styles.mobileList}>
          {items.length ? (
            items.map((t) => (
              <div key={t.id} style={styles.mobileTransactionCard}>
                <div style={styles.mobileTransactionTop}>
                  <div>
                    <div style={styles.mobileTransactionTitle}>{t.description}</div>
                    <div style={styles.mobileTransactionMeta}>
                      {t.date} • {t.category}
                    </div>
                  </div>
                  <span
                    style={{
                      ...styles.statusPill,
                      ...(t.status === 'Successful'
                        ? styles.statusPillSuccess
                        : styles.statusPillWarning),
                    }}
                  >
                    {t.status}
                  </span>
                </div>
                <div style={styles.mobileTransactionAmounts}>
                  <div style={styles.mobileAmountBlock}>
                    <span style={styles.amountLabel}>Debit</span>
                    <strong style={styles.tdDebit}>
                      {t.debit ? formatCurrency(t.debit) : '—'}
                    </strong>
                  </div>
                  <div style={styles.mobileAmountBlock}>
                    <span style={styles.amountLabel}>Credit</span>
                    <strong style={styles.tdCredit}>
                      {t.credit ? formatCurrency(t.credit) : '—'}
                    </strong>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.emptyState}>No transactions match your current search/filter.</div>
          )}
        </div>
      );
    }

    return (
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Date</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Debit</th>
              <th style={styles.th}>Credit</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((t) => (
                <tr key={t.id} style={styles.tr}>
                  <td style={styles.td}>{t.date}</td>
                  <td style={styles.tdStrong}>{t.description}</td>
                  <td style={styles.td}>
                    <span style={styles.categoryPill}>{t.category}</span>
                  </td>
                  <td style={styles.tdDebit}>
                    {t.debit ? formatCurrency(t.debit) : '—'}
                  </td>
                  <td style={styles.tdCredit}>
                    {t.credit ? formatCurrency(t.credit) : '—'}
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.statusPill,
                        ...(t.status === 'Successful'
                          ? styles.statusPillSuccess
                          : styles.statusPillWarning),
                      }}
                    >
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={styles.emptyState}>
                  No transactions match your current search/filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderOverviewSection = () => (
    <>
      <motion.section
        initial={{ opacity: 0, y: 22 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05, duration: 0.35 }}
        style={{
          ...styles.hero,
          ...(isTablet ? styles.heroTablet : {}),
        }}
      >
        <div style={styles.heroLeft}>
          <div style={styles.heroBadge}>
            <Sparkles size={15} />
            Cooperative wallet is active
          </div>
          <h1
            style={{
              ...styles.heroTitle,
              ...(isMobile ? styles.heroTitleMobile : {}),
            }}
          >
            Welcome back, {user?.name || 'Member'}
          </h1>
          <p style={styles.heroText}>
            Your contributions are growing steadily. You also have active dividend
            opportunities and loan access available this cycle.
          </p>

          <div style={styles.heroMetrics}>
            <div style={styles.heroMetricItem}>
              <span style={styles.heroMetricLabel}>Wallet Balance</span>
              <strong style={styles.heroMetricValue}>{formatCurrency(balance)}</strong>
            </div>
            <div style={styles.heroMetricItem}>
              <span style={styles.heroMetricLabel}>Shares Owned</span>
              <strong style={styles.heroMetricValue}>{sharesOwned} Units</strong>
            </div>
          </div>
        </div>

        <div
          style={{
            ...styles.heroRight,
            ...(isMobile ? styles.heroRightMobile : {}),
          }}
        >
          <motion.div whileHover={{ y: -4 }} transition={{ duration: 0.2 }} style={styles.heroGlassCard}>
            <div style={styles.heroCardTop}>
              <span style={styles.heroCardLabel}>Next Loan Due</span>
              <CreditCard size={18} />
            </div>
            <div style={styles.heroCardAmount}>{formatCurrency(loanStatus.remaining)}</div>
            <div style={styles.heroCardMeta}>Due {loanStatus.nextDue}</div>
          </motion.div>

          <motion.div
            whileHover={{ y: -4 }}
            transition={{ duration: 0.2 }}
            style={styles.heroGlassCardSecondary}
          >
            <div style={styles.heroCardTop}>
              <span style={styles.heroCardLabel}>Dividend Ready</span>
              <CircleDollarSign size={18} />
            </div>
            <div style={styles.heroCardAmount}>{formatCurrency(dividend.amount)}</div>
            <div style={styles.heroCardMeta}>{dividend.status}</div>
          </motion.div>
        </div>
      </motion.section>

      <section
        style={{
          ...styles.statsGrid,
          ...(isMobile ? styles.statsGridMobile : {}),
        }}
      >
        {[
          {
            title: 'Total Savings',
            value: formatCurrency(totalSaved),
            sub: 'All-time member contributions',
            icon: PiggyBank,
            accent: '#16a34a',
          },
          {
            title: 'Monthly Contribution',
            value: formatCurrency(monthlyContribution),
            sub: 'Payroll deduction this cycle',
            icon: Wallet,
            accent: '#0f766e',
          },
          {
            title: 'Dividend Balance',
            value: formatCurrency(dividend.amount),
            sub: `${dividend.year} allocation available`,
            icon: BadgePercent,
            accent: '#7c3aed',
          },
          {
            title: 'Pending Requests',
            value: String(pendingApproval),
            sub: 'Items awaiting action',
            icon: Clock3,
            accent: '#f59e0b',
          },
        ].map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 + index * 0.05, duration: 0.28 }}
              whileHover={{ y: -5 }}
              style={styles.statCard}
            >
              <div
                style={{
                  ...styles.statIconWrap,
                  background: `linear-gradient(135deg, ${card.accent}, #0f172a)`,
                }}
              >
                <Icon size={18} color="#fff" />
              </div>
              <div style={styles.statTitle}>{card.title}</div>
              <div style={styles.statValue}>{card.value}</div>
              <div style={styles.statSub}>{card.sub}</div>
            </motion.div>
          );
        })}
      </section>

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Quick Actions</div>
                <div style={styles.cardSub}>
                  Modern cooperative functions built for speed and convenience.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.quickActionsGrid,
                ...(isMobile ? styles.quickActionsGridMobile : {}),
              }}
            >
              {quickActions.map((item) => {
                const Icon = item.icon;
                const active = activeAction === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveAction(item.id)}
                    style={{
                      ...styles.quickActionButton,
                      ...(active ? styles.quickActionButtonActive : {}),
                    }}
                  >
                    <div
                      style={{
                        ...styles.quickActionIcon,
                        ...(active ? styles.quickActionIconActive : {}),
                      }}
                    >
                      <Icon size={18} />
                    </div>
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>

            <motion.div
              key={activeAction}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              style={{
                ...styles.actionPanel,
                borderColor: `${currentActionConfig.accent}22`,
              }}
            >
              <div style={styles.actionPanelTop}>
                <div>
                  <div style={styles.actionTitle}>{currentActionConfig.title}</div>
                  <div style={styles.actionDescription}>
                    {currentActionConfig.description}
                  </div>
                </div>
                <div
                  style={{
                    ...styles.actionDot,
                    backgroundColor: currentActionConfig.accent,
                  }}
                />
              </div>

              {renderActionPanel()}
            </motion.div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.34 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Transaction Center</div>
                <div style={styles.cardSub}>
                  Search, filter, and monitor all member wallet activities.
                </div>
              </div>

              <div style={styles.filterWrap}>
                {['all', 'contribution', 'loan', 'dividend', 'wallet', 'fee'].map(
                  (type) => (
                    <button
                      key={type}
                      onClick={() => setFilterType(type)}
                      style={{
                        ...styles.filterButton,
                        ...(filterType === type ? styles.filterButtonActive : {}),
                      }}
                    >
                      {type}
                    </button>
                  )
                )}
              </div>
            </div>

            <div style={styles.transactionSummary}>
              <div style={styles.summaryPillGreen}>
                <ArrowDownLeft size={15} />
                Total Inflow: {formatCurrency(creditedTotal)}
              </div>
              <div style={styles.summaryPillOrange}>
                <ArrowUpRight size={15} />
                Total Outflow: {formatCurrency(debitedTotal)}
              </div>
            </div>

            {renderTransactionList(filteredTransactions.slice(0, isMobile ? 4 : 5))}
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.38 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Investment Opportunities</div>
                <div style={styles.cardSub}>
                  Cooperative-backed products and projects you can subscribe to.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.investmentGrid,
                ...(isMobile ? styles.investmentGridMobile : {}),
              }}
            >
              {investmentOptions.map((item) => (
                <motion.div key={item.title} whileHover={{ y: -4 }} style={styles.investmentCard}>
                  <div style={styles.investmentTag}>{item.tag}</div>
                  <div style={styles.investmentTitle}>{item.title}</div>
                  <div style={styles.investmentMeta}>{item.roi}</div>
                  <div style={styles.investmentMinimum}>{item.minimum}</div>
                  <button style={styles.secondaryButton}>Subscribe</button>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Savings Goal Tracker</div>
                <div style={styles.cardSub}>Track your cooperative growth target.</div>
              </div>
              <Target size={18} color="#16a34a" />
            </div>

            <div style={styles.goalAmount}>{formatCurrency(totalSaved)}</div>
            <div style={styles.goalLabel}>Goal Target: {formatCurrency(savingsGoal)}</div>

            <div style={styles.progressTrack}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${savingsProgress}%` }}
                transition={{ duration: 0.8 }}
                style={styles.progressFillGreen}
              />
            </div>

            <div style={styles.progressMeta}>
              <span>{savingsProgress}% completed</span>
              <span>{formatCurrency(savingsGoal - totalSaved)} to goal</span>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.32 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Loan Overview</div>
                <div style={styles.cardSub}>
                  Active facility performance and repayment progress.
                </div>
              </div>
              <HandCoins size={18} color="#15803d" />
            </div>

            <div style={styles.loanMetrics}>
              <div style={styles.metricRow}>
                <span>Loan Type</span>
                <strong>{loanStatus.type}</strong>
              </div>
              <div style={styles.metricRow}>
                <span>Outstanding</span>
                <strong>{formatCurrency(loanStatus.remaining)}</strong>
              </div>
              <div style={styles.metricRow}>
                <span>Next Due Date</span>
                <strong>{loanStatus.nextDue}</strong>
              </div>
            </div>

            <div style={styles.progressTrack}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${loanProgress}%` }}
                transition={{ duration: 0.8 }}
                style={styles.progressFillDark}
              />
            </div>

            <div style={styles.progressMeta}>
              <span>{loanProgress}% repaid</span>
              <span>{formatCurrency(loanRepaid)} settled</span>
            </div>

            <button style={styles.primaryButton}>Request Another Loan</button>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.36 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Dividend Summary</div>
                <div style={styles.cardSub}>
                  Annual returns and reinvestment-ready balances.
                </div>
              </div>
              <BadgePercent size={18} color="#7c3aed" />
            </div>

            <div style={styles.dividendAmount}>{formatCurrency(dividend.amount)}</div>
            <div style={styles.dividendStatus}>{dividend.status}</div>

            <div
              style={{
                ...styles.formGrid,
                ...(isMobile ? styles.formGridMobile : {}),
              }}
            >
              <div style={styles.miniInfoCard}>
                <span style={styles.miniInfoLabel}>Financial Year</span>
                <strong style={styles.miniInfoValue}>{dividend.year}</strong>
              </div>
              <div style={styles.miniInfoCard}>
                <span style={styles.miniInfoLabel}>Reinvestable</span>
                <strong style={styles.miniInfoValue}>
                  {formatCurrency(dividend.reinvestable)}
                </strong>
              </div>
            </div>

            <div style={styles.inlineButtons}>
              <button style={styles.primaryButton}>Reinvest</button>
              <button style={styles.secondaryButton}>Withdraw</button>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Announcements & Activities</div>
                <div style={styles.cardSub}>
                  Stay updated on cooperative programs, meetings, and votes.
                </div>
              </div>
            </div>

            <div style={styles.announcementList}>
              {announcements.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} style={styles.announcementItem}>
                    <div style={styles.announcementIcon}>
                      <Icon size={17} />
                    </div>
                    <div>
                      <div style={styles.announcementTitle}>{item.title}</div>
                      <div style={styles.announcementText}>{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.44 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Member Profile</div>
                <div style={styles.cardSub}>
                  Your cooperative identity and banking profile.
                </div>
              </div>
              <UserRound size={18} color="#0f766e" />
            </div>

            <div style={styles.profileBlock}>
              <div style={styles.profileRow}>
                <span>Full Name</span>
                <strong>{profileInfo.fullName}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Member ID</span>
                <strong>{profileInfo.memberId}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Primary Bank</span>
                <strong>{profileInfo.bank}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>KYC Status</span>
                <strong style={{ color: '#16a34a' }}>{profileInfo.kyc}</strong>
              </div>
            </div>

            <button style={styles.secondaryButton}>Update Profile</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderWalletSection = () => (
    <>
      {renderSectionHero([
        { label: 'Available Balance', value: formatCurrency(balance) },
        { label: 'Dividend Wallet', value: formatCurrency(dividend.amount) },
        { label: 'Share Capital', value: formatCurrency(125000) },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Wallet Accounts</div>
                <div style={styles.cardSub}>
                  Dedicated balances for savings, dividends, and cooperative capital.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.walletGrid,
                ...(isMobile ? styles.walletGridMobile : {}),
              }}
            >
              {walletAccounts.map((item) => (
                <div key={item.title} style={styles.walletCard}>
                  <div style={styles.walletCardTop}>
                    <span style={styles.walletTag}>{item.tag}</span>
                    <Wallet size={18} />
                  </div>
                  <div style={styles.walletCardTitle}>{item.title}</div>
                  <div style={styles.walletCardAmount}>{formatCurrency(item.balance)}</div>
                  <div style={styles.walletCardText}>{item.description}</div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeaderSplit}>
              <div>
                <div style={styles.cardTitle}>Wallet Actions</div>
                <div style={styles.cardSub}>
                  Top up or request withdrawal from your cooperative wallet.
                </div>
              </div>
              <div style={styles.inlineButtons}>
                <button
                  onClick={() => setActiveAction('deposit')}
                  style={styles.secondaryButton}
                >
                  Fund Wallet
                </button>
                <button
                  onClick={() => setActiveAction('withdraw')}
                  style={styles.secondaryButton}
                >
                  Withdraw
                </button>
              </div>
            </div>

            <motion.div
              key={activeAction}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              style={styles.actionPanel}
            >
              {renderActionPanel()}
            </motion.div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Recent Wallet Activity</div>
                <div style={styles.cardSub}>
                  Latest credits and debits posted to your member wallet.
                </div>
              </div>
            </div>
            {renderTransactionList(
              filteredTransactions.filter((item) =>
                ['wallet', 'withdrawal', 'dividend'].includes(item.category)
              )
            )}
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Linked Bank Channel</div>
                <div style={styles.cardSub}>
                  Withdrawals are routed to your registered bank profile.
                </div>
              </div>
              <Building2 size={18} color="#16a34a" />
            </div>

            <div style={styles.profileBlock}>
              <div style={styles.profileRow}>
                <span>Bank Name</span>
                <strong>Zenith Bank</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Account Number</span>
                <strong>•••• 4102</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Settlement Mode</span>
                <strong>Primary Withdrawal Route</strong>
              </div>
            </div>

            <button style={styles.secondaryButton}>Change Bank Details</button>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Wallet Insights</div>
                <div style={styles.cardSub}>
                  Quick indicators for your current wallet health.
                </div>
              </div>
              <Activity size={18} color="#0f766e" />
            </div>

            <div style={styles.metricStack}>
              <div style={styles.metricChip}>
                <span>Wallet Ready</span>
                <strong>Active</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Auto Contribution</span>
                <strong>{formatCurrency(monthlyContribution)}</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Pending Withdrawal</span>
                <strong>{pendingApproval}</strong>
              </div>
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderTransactionsSection = () => (
    <>
      {renderSectionHero([
        { label: 'Total Inflow', value: formatCurrency(creditedTotal) },
        { label: 'Total Outflow', value: formatCurrency(debitedTotal) },
        { label: 'Records', value: String(filteredTransactions.length) },
      ])}

      <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
        <div style={styles.cardHeaderSplit}>
          <div>
            <div style={styles.cardTitle}>Transaction Ledger</div>
            <div style={styles.cardSub}>
              Filter by category and review contribution, loan, wallet, fee, and dividend history.
            </div>
          </div>

          <div style={styles.filterWrap}>
            {['all', 'contribution', 'loan', 'dividend', 'wallet', 'fee', 'withdrawal'].map(
              (type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  style={{
                    ...styles.filterButton,
                    ...(filterType === type ? styles.filterButtonActive : {}),
                  }}
                >
                  {type}
                </button>
              )
            )}
          </div>
        </div>

        <div style={styles.transactionSummary}>
          <div style={styles.summaryPillGreen}>
            <ArrowDownLeft size={15} />
            Total Inflow: {formatCurrency(creditedTotal)}
          </div>
          <div style={styles.summaryPillOrange}>
            <ArrowUpRight size={15} />
            Total Outflow: {formatCurrency(debitedTotal)}
          </div>
        </div>

        {renderTransactionList(filteredTransactions)}
      </motion.section>
    </>
  );

  const renderLoansSection = () => (
    <>
      {renderSectionHero([
        { label: 'Outstanding Loan', value: formatCurrency(loanStatus.remaining) },
        { label: 'Eligibility', value: loanStatus.eligibility },
        { label: 'Score', value: loanStatus.score },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Active Loan</div>
                <div style={styles.cardSub}>
                  Current loan status, remaining balance, and repayment progress.
                </div>
              </div>
            </div>

            <div style={styles.loanFocusCard}>
              <div style={styles.loanFocusTop}>
                <span style={styles.walletTag}>{loanStatus.type}</span>
                <HandCoins size={20} />
              </div>
              <div style={styles.loanFocusAmount}>{formatCurrency(loanStatus.remaining)}</div>
              <div style={styles.loanFocusText}>Outstanding from {formatCurrency(loanStatus.amount)}</div>

              <div style={styles.progressTrack}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${loanProgress}%` }}
                  transition={{ duration: 0.8 }}
                  style={styles.progressFillDark}
                />
              </div>

              <div style={styles.progressMeta}>
                <span>{loanProgress}% repaid</span>
                <span>Next due {loanStatus.nextDue}</span>
              </div>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Repayment Schedule</div>
                <div style={styles.cardSub}>
                  Upcoming installments and loan cycle timeline.
                </div>
              </div>
            </div>

            <div style={styles.scheduleList}>
              {repaymentSchedule.map((item) => (
                <div key={item.label} style={styles.scheduleItem}>
                  <div>
                    <div style={styles.scheduleTitle}>{item.label}</div>
                    <div style={styles.scheduleText}>{item.due}</div>
                  </div>
                  <div style={styles.scheduleRight}>
                    <strong>{formatCurrency(item.amount)}</strong>
                    <span style={styles.scheduleTag}>{item.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Loan Products</div>
                <div style={styles.cardSub}>
                  Demo loan packages available to eligible members.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.investmentGrid,
                ...(isMobile ? styles.investmentGridMobile : {}),
              }}
            >
              {loanProducts.map((item) => (
                <div key={item.title} style={styles.investmentCard}>
                  <div style={styles.investmentTag}>Available</div>
                  <div style={styles.investmentTitle}>{item.title}</div>
                  <div style={styles.investmentMinimum}>{item.limit}</div>
                  <div style={styles.loanProductMeta}>
                    <span>{item.tenor}</span>
                    <span>{item.rate}</span>
                  </div>
                  <button style={styles.primaryButton}>Apply Now</button>
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Loan Readiness</div>
                <div style={styles.cardSub}>
                  Your cooperative standing and loan access readiness.
                </div>
              </div>
              <ShieldCheck size={18} color="#16a34a" />
            </div>

            <div style={styles.metricStack}>
              <div style={styles.metricChip}>
                <span>Contribution History</span>
                <strong>6+ Months</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Loan-to-Savings Ratio</span>
                <strong>Within Limit</strong>
              </div>
              <div style={styles.metricChip}>
                <span>Approval Path</span>
                <strong>Admin Review</strong>
              </div>
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderDividendsSection = () => (
    <>
      {renderSectionHero([
        { label: 'Dividend Amount', value: formatCurrency(dividend.amount) },
        { label: 'Reinvestable', value: formatCurrency(dividend.reinvestable) },
        { label: 'Last Paid', value: dividend.lastPaid },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Dividend Summary</div>
                <div style={styles.cardSub}>
                  View credited returns and decide whether to reinvest or withdraw.
                </div>
              </div>
            </div>

            <div style={styles.dividendFeatureCard}>
              <div style={styles.dividendFeatureAmount}>{formatCurrency(dividend.amount)}</div>
              <div style={styles.dividendStatus}>{dividend.status}</div>
              <div style={styles.inlineButtons}>
                <button style={styles.primaryButton}>Reinvest Dividend</button>
                <button style={styles.secondaryButton}>Withdraw Dividend</button>
              </div>
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Dividend History</div>
                <div style={styles.cardSub}>
                  Annual dividend records and payout status.
                </div>
              </div>
            </div>

            <div style={styles.scheduleList}>
              {dividendHistory.map((item) => (
                <div key={item.year} style={styles.scheduleItem}>
                  <div>
                    <div style={styles.scheduleTitle}>FY {item.year}</div>
                    <div style={styles.scheduleText}>{item.date}</div>
                  </div>
                  <div style={styles.scheduleRight}>
                    <strong>{formatCurrency(item.amount)}</strong>
                    <span style={styles.scheduleTag}>{item.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Dividend Notes</div>
                <div style={styles.cardSub}>
                  Key rules for cooperative profit allocation and member returns.
                </div>
              </div>
              <BadgePercent size={18} color="#7c3aed" />
            </div>

            <div style={styles.noteList}>
              <div style={styles.noteItem}>Returns are based on cooperative performance and contribution weight.</div>
              <div style={styles.noteItem}>Members may reinvest payouts into shares or savings.</div>
              <div style={styles.noteItem}>Withdrawals are routed to your registered settlement account.</div>
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderInvestmentsSection = () => (
    <>
      {renderSectionHero([
        { label: 'Portfolio Value', value: formatCurrency(225000) },
        { label: 'Projected Returns', value: formatCurrency(34800) },
        { label: 'Live Offers', value: '3 Products' },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Marketplace</div>
                <div style={styles.cardSub}>
                  Subscribe to cooperative-backed investment products.
                </div>
              </div>
            </div>

            <div
              style={{
                ...styles.investmentGrid,
                ...(isMobile ? styles.investmentGridMobile : {}),
              }}
            >
              {investmentOptions.map((item) => (
                <motion.div key={item.title} whileHover={{ y: -4 }} style={styles.investmentCard}>
                  <div style={styles.walletCardTop}>
                    <span style={styles.investmentTag}>{item.tag}</span>
                    <TrendingUp size={18} />
                  </div>
                  <div style={styles.investmentTitle}>{item.title}</div>
                  <div style={styles.investmentMeta}>{item.roi}</div>
                  <div style={styles.investmentMinimum}>{item.minimum}</div>
                  <div style={styles.scheduleTag}>{item.status}</div>
                  <div style={{ height: 8 }} />
                  <div style={styles.walletCardText}>{item.units}</div>
                  <div style={{ height: 14 }} />
                  <button style={styles.primaryButton}>Subscribe</button>
                </motion.div>
              ))}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Portfolio Snapshot</div>
                <div style={styles.cardSub}>
                  Summary of your current investment participation.
                </div>
              </div>
            </div>

            <div style={styles.metricStack}>
              {portfolioSummary.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} style={styles.metricCard}>
                    <Icon size={17} />
                    <div>
                      <div style={styles.metricTitle}>{item.title}</div>
                      <div style={styles.metricValue}>{item.value}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderProfileSection = () => (
    <>
      {renderSectionHero([
        { label: 'Member ID', value: profileInfo.memberId },
        { label: 'Department', value: profileInfo.department },
        { label: 'KYC', value: profileInfo.kyc },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Personal Profile</div>
                <div style={styles.cardSub}>
                  Your personal, employment, and cooperative identity information.
                </div>
              </div>
              <IdCard size={18} color="#0f766e" />
            </div>

            <div style={styles.profileBlock}>
              <div style={styles.profileRow}>
                <span>Full Name</span>
                <strong>{profileInfo.fullName}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Member ID</span>
                <strong>{profileInfo.memberId}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Staff ID</span>
                <strong>{profileInfo.staffId}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Department</span>
                <strong>{profileInfo.department}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Phone</span>
                <strong>{profileInfo.phone}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Email</span>
                <strong>{profileInfo.email}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Address</span>
                <strong>{profileInfo.address}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Bank</span>
                <strong>{profileInfo.bank}</strong>
              </div>
            </div>

            <button style={styles.primaryButton}>Request Profile Update</button>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Next of Kin</div>
                <div style={styles.cardSub}>
                  Emergency and beneficiary details on file.
                </div>
              </div>
              <UserRound size={18} color="#16a34a" />
            </div>

            <div style={styles.profileBlock}>
              <div style={styles.profileRow}>
                <span>Name</span>
                <strong>{profileInfo.nextOfKin}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>Phone</span>
                <strong>{profileInfo.nextOfKinPhone}</strong>
              </div>
              <div style={styles.profileRow}>
                <span>KYC Status</span>
                <strong style={{ color: '#16a34a' }}>{profileInfo.kyc}</strong>
              </div>
            </div>

            <button style={styles.secondaryButton}>Update Next of Kin</button>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderSecuritySection = () => (
    <>
      {renderSectionHero([
        { label: '2FA Status', value: 'Enabled' },
        { label: 'Trusted Devices', value: '2 Devices' },
        { label: 'Current Session', value: 'Verified' },
      ])}

      <section
        style={{
          ...styles.contentGrid,
          ...(isTablet ? styles.contentGridTablet : {}),
        }}
      >
        <div style={styles.leftColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Security Controls</div>
                <div style={styles.cardSub}>
                  Account protection, device trust, and sign-in safeguards.
                </div>
              </div>
              <ShieldAlert size={18} color="#0f766e" />
            </div>

            <div style={styles.securityGrid}>
              {securitySettings.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} style={styles.securityCard}>
                    <div style={styles.securityCardTop}>
                      <div style={styles.securityIcon}>
                        <Icon size={18} />
                      </div>
                      <span style={styles.walletTag}>{item.status}</span>
                    </div>
                    <div style={styles.securityTitle}>{item.title}</div>
                    <div style={styles.securityText}>{item.desc}</div>
                  </div>
                );
              })}
            </div>
          </motion.section>

          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Login Activity</div>
                <div style={styles.cardSub}>
                  Trusted session history and recent account actions.
                </div>
              </div>
            </div>

            <div style={styles.scheduleList}>
              {loginActivity.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={`${item.device}-${item.time}`} style={styles.scheduleItem}>
                    <div style={styles.scheduleWithIcon}>
                      <div style={styles.securityIconSmall}>
                        <Icon size={16} />
                      </div>
                      <div>
                        <div style={styles.scheduleTitle}>{item.device}</div>
                        <div style={styles.scheduleText}>
                          {item.time} • {item.location}
                        </div>
                      </div>
                    </div>
                    <span style={styles.scheduleTag}>{item.status}</span>
                  </div>
                );
              })}
            </div>
          </motion.section>
        </div>

        <div style={styles.rightColumn}>
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} style={styles.card}>
            <div style={styles.cardHeader}>
              <div>
                <div style={styles.cardTitle}>Recommended Actions</div>
                <div style={styles.cardSub}>
                  Keep your member profile safe and secure.
                </div>
              </div>
              <ShieldCheck size={18} color="#16a34a" />
            </div>

            <div style={styles.noteList}>
              <div style={styles.noteItem}>Keep two-factor authentication enabled.</div>
              <div style={styles.noteItem}>Review session history regularly.</div>
              <div style={styles.noteItem}>Update password periodically for account protection.</div>
            </div>

            <div style={styles.inlineButtons}>
              <button style={styles.primaryButton}>Change Password</button>
              <button style={styles.secondaryButton}>Review Devices</button>
            </div>
          </motion.section>
        </div>
      </section>
    </>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'wallet':
        return renderWalletSection();
      case 'transactions':
        return renderTransactionsSection();
      case 'loans':
        return renderLoansSection();
      case 'dividends':
        return renderDividendsSection();
      case 'investments':
        return renderInvestmentsSection();
      case 'profile':
        return renderProfileSection();
      case 'security':
        return renderSecuritySection();
      case 'overview':
      default:
        return renderOverviewSection();
    }
  };

  const sidebar = (
    <motion.aside
      initial={{ x: -30, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.35 }}
      style={{
        ...styles.sidebar,
        ...(isTablet ? styles.sidebarTablet : {}),
      }}
    >
      <div style={styles.brandWrap}>
        <div style={styles.logoBadge}>
          <Landmark size={20} />
        </div>
        <div>
          <div style={styles.brandTitle}>FAAN Cooperative</div>
          <div style={styles.brandSub}>Member Experience Portal</div>
        </div>
      </div>

      <div style={styles.memberCard}>
        <div style={styles.memberAvatar}>
          {(user?.name || 'M').slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div style={styles.memberName}>{user?.name || 'Member'}</div>
          <div style={styles.memberMeta}>Active Cooperative Member</div>
        </div>
      </div>

      <div style={styles.navWrap}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = activeSection === item.id;

          return (
            <button
              key={item.id}
              onClick={() => goToSection(item.id)}
              style={{
                ...styles.navButton,
                ...(active ? styles.navButtonActive : {}),
              }}
            >
              <span style={styles.navLeft}>
                <Icon size={18} />
                {item.label}
              </span>
              <ChevronRight size={16} />
            </button>
          );
        })}
      </div>

      <div style={styles.sidebarBottomCard}>
        <div style={styles.sidebarBottomTitle}>Cooperative Health</div>
        <div style={styles.sidebarBottomText}>
          Your account is in good standing. Eligible for savings boost, voting, and loan review.
        </div>
      </div>
    </motion.aside>
  );

  return (
    <div style={styles.page}>
      <div style={styles.bgGlowOne} />
      <div style={styles.bgGlowTwo} />

      {isTablet && (
        <button onClick={() => setSidebarOpen(true)} style={styles.mobileMenuButton}>
          <Menu size={20} />
        </button>
      )}

      <AnimatePresence>
        {isTablet && sidebarOpen && (
          <>
            <motion.div
              style={styles.overlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              style={styles.mobileSidebarShell}
              initial={{ x: -340 }}
              animate={{ x: 0 }}
              exit={{ x: -340 }}
              transition={{ duration: 0.28 }}
            >
              <button onClick={() => setSidebarOpen(false)} style={styles.closeButton}>
                <X size={18} />
              </button>
              {sidebar}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {!isTablet && sidebar}

      <main
        style={{
          ...styles.main,
          ...(isTablet ? styles.mainTablet : {}),
        }}
      >
        <motion.header
          initial={{ y: -18, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.35 }}
          style={{
            ...styles.topbar,
            ...(isTablet ? styles.topbarTablet : {}),
          }}
        >
          <div style={styles.topbarTextWrap}>
            <div style={styles.pageTitle}>{currentSection.title}</div>
            <div style={styles.pageSubtitle}>{currentSection.subtitle}</div>
          </div>

          <div
            style={{
              ...styles.topbarActions,
              ...(isMobile ? styles.topbarActionsMobile : {}),
            }}
          >
            <div
              style={{
                ...styles.searchBox,
                ...(isMobile ? styles.searchBoxMobile : {}),
              }}
            >
              <Search size={18} color="#64748b" />
              <input
                placeholder="Search transactions or activities"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>

            <button style={styles.iconButton}>
              <Bell size={18} />
            </button>

            <button onClick={logout} style={styles.logoutButton}>
              <LogOut size={17} />
              {!isMobile && 'Logout'}
            </button>
          </div>
        </motion.header>

        {renderSectionContent()}
      </main>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    background:
      'radial-gradient(circle at top left, rgba(34,197,94,0.12), transparent 24%), linear-gradient(180deg, #f7fff9 0%, #f8fafc 100%)',
    position: 'relative',
    overflow: 'hidden',
    color: '#0f172a',
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  bgGlowOne: {
    position: 'absolute',
    top: -120,
    right: -80,
    width: 280,
    height: 280,
    background: 'rgba(34,197,94,0.18)',
    filter: 'blur(80px)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  bgGlowTwo: {
    position: 'absolute',
    bottom: -120,
    left: -60,
    width: 260,
    height: 260,
    background: 'rgba(16,185,129,0.12)',
    filter: 'blur(90px)',
    borderRadius: '50%',
    pointerEvents: 'none',
  },
  sidebar: {
    width: 290,
    minWidth: 290,
    padding: 20,
    borderRight: '1px solid rgba(15,23,42,0.06)',
    background: 'rgba(255,255,255,0.76)',
    backdropFilter: 'blur(16px)',
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    position: 'relative',
    zIndex: 3,
  },
  sidebarTablet: {
    width: '100%',
    minWidth: '100%',
    borderRight: 'none',
    height: '100%',
  },
  mobileSidebarShell: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 340,
    maxWidth: '88vw',
    height: '100vh',
    background: '#fff',
    zIndex: 60,
    boxShadow: '20px 0 60px rgba(15,23,42,0.18)',
  },
  mobileMenuButton: {
    position: 'fixed',
    top: 16,
    left: 16,
    zIndex: 25,
    width: 44,
    height: 44,
    borderRadius: 14,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.94)',
    backdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 12px 30px rgba(15,23,42,0.08)',
  },
  closeButton: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 38,
    height: 38,
    borderRadius: 12,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(2,6,23,0.36)',
    zIndex: 50,
  },
  brandWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingBottom: 12,
    borderBottom: '1px solid rgba(15,23,42,0.06)',
  },
  logoBadge: {
    width: 44,
    height: 44,
    borderRadius: 14,
    background: 'linear-gradient(135deg, #16a34a, #064e3b)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 14px 30px rgba(22,163,74,0.24)',
  },
  brandTitle: {
    fontSize: fs(16),
    fontWeight: 800,
    color: '#052e16',
  },
  brandSub: {
    fontSize: fs(12),
    color: '#64748b',
  },
  memberCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 18,
    background: 'linear-gradient(135deg, #effff4, #f8fafc)',
    border: '1px solid rgba(34,197,94,0.12)',
  },
  memberAvatar: {
    width: 50,
    height: 50,
    borderRadius: 16,
    background: 'linear-gradient(135deg, #16a34a, #15803d)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: fs(18),
  },
  memberName: {
    fontWeight: 700,
    fontSize: fs(15),
  },
  memberMeta: {
    fontSize: fs(12),
    color: '#64748b',
    marginTop: 2,
  },
  navWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  navButton: {
    width: '100%',
    border: '1px solid transparent',
    background: 'transparent',
    borderRadius: 16,
    padding: '13px 14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    color: '#334155',
    transition: 'all 0.2s ease',
  },
  navButtonActive: {
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    boxShadow: '0 12px 28px rgba(22,163,74,0.22)',
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontWeight: 600,
    fontSize: fs(14),
  },
  sidebarBottomCard: {
    marginTop: 'auto',
    padding: 16,
    borderRadius: 18,
    background: '#0f172a',
    color: '#fff',
  },
  sidebarBottomTitle: {
    fontWeight: 700,
    fontSize: fs(14),
    marginBottom: 8,
  },
  sidebarBottomText: {
    fontSize: fs(13),
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.75)',
  },
  main: {
    flex: 1,
    padding: '24px 24px 28px',
    position: 'relative',
    zIndex: 2,
    overflowX: 'hidden',
  },
  mainTablet: {
    padding: '76px 16px 24px',
  },
  topbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 22,
  },
  topbarTablet: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  topbarTextWrap: {
    minWidth: 0,
  },
  pageTitle: {
    fontSize: fs(28),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 6,
  },
  pageSubtitle: {
    color: '#64748b',
    fontSize: fs(14),
    maxWidth: 720,
    lineHeight: 1.6,
  },
  topbarActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  topbarActionsMobile: {
    width: '100%',
  },
  searchBox: {
    minWidth: 260,
    height: 46,
    padding: '0 14px',
    borderRadius: 14,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.85)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    boxShadow: '0 10px 30px rgba(15,23,42,0.04)',
  },
  searchBoxMobile: {
    minWidth: 0,
    width: 'calc(100% - 112px)',
    flex: 1,
  },
  searchInput: {
    border: 'none',
    outline: 'none',
    background: 'transparent',
    width: '100%',
    minWidth: 0,
    fontSize: fs(14),
    color: '#0f172a',
  },
  iconButton: {
    width: 46,
    height: 46,
    borderRadius: 14,
    border: '1px solid rgba(15,23,42,0.08)',
    background: 'rgba(255,255,255,0.88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  },
  logoutButton: {
    height: 46,
    padding: '0 16px',
    borderRadius: 14,
    border: 'none',
    background: '#0f172a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontWeight: 700,
    fontSize: fs(14),
    cursor: 'pointer',
    boxShadow: '0 14px 30px rgba(15,23,42,0.15)',
    flexShrink: 0,
  },
  pageHero: {
    borderRadius: 26,
    padding: 22,
    marginBottom: 22,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.95), rgba(15,23,42,0.94))',
    color: '#fff',
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 1.2fr) minmax(220px, 0.8fr)',
    gap: 16,
    boxShadow: '0 30px 70px rgba(5,150,105,0.18)',
  },
  pageHeroTablet: {
    gridTemplateColumns: '1fr',
  },
  pageHeroLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  pageHeroBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    width: 'fit-content',
    padding: '8px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.14)',
    fontSize: fs(12),
    fontWeight: 700,
  },
  pageHeroTitle: {
    margin: 0,
    fontSize: fs(30),
    lineHeight: 1.15,
    fontWeight: 800,
  },
  pageHeroTitleMobile: {
    fontSize: fs(24),
  },
  pageHeroText: {
    margin: 0,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.7,
    fontSize: fs(14.5),
  },
  pageHeroStats: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },
  pageHeroStatsMobile: {
    gridTemplateColumns: '1fr',
  },
  pageHeroStatCard: {
    padding: 14,
    borderRadius: 18,
    background: 'rgba(255,255,255,0.12)',
  },
  pageHeroStatLabel: {
    display: 'block',
    fontSize: fs(12),
    color: 'rgba(255,255,255,0.72)',
    marginBottom: 6,
  },
  pageHeroStatValue: {
    fontSize: fs(16),
    fontWeight: 800,
    lineHeight: 1.45,
  },
  hero: {
    borderRadius: 28,
    padding: 24,
    marginBottom: 22,
    background: 'linear-gradient(135deg, rgba(5,150,105,0.95), rgba(15,23,42,0.94))',
    color: '#fff',
    display: 'grid',
    gridTemplateColumns: 'minmax(280px, 1.4fr) minmax(260px, 0.9fr)',
    gap: 18,
    boxShadow: '0 30px 70px rgba(5,150,105,0.22)',
  },
  heroTablet: {
    gridTemplateColumns: '1fr',
  },
  heroLeft: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  heroBadge: {
    display: 'inline-flex',
    width: 'fit-content',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    borderRadius: 999,
    background: 'rgba(255,255,255,0.14)',
    fontSize: fs(12),
    fontWeight: 700,
  },
  heroTitle: {
    margin: 0,
    fontSize: fs(34),
    lineHeight: 1.15,
    fontWeight: 800,
  },
  heroTitleMobile: {
    fontSize: fs(26),
  },
  heroText: {
    margin: 0,
    maxWidth: 680,
    color: 'rgba(255,255,255,0.82)',
    lineHeight: 1.7,
    fontSize: fs(14.5),
  },
  heroMetrics: {
    display: 'flex',
    gap: 14,
    flexWrap: 'wrap',
    marginTop: 4,
  },
  heroMetricItem: {
    padding: '12px 14px',
    borderRadius: 18,
    background: 'rgba(255,255,255,0.12)',
    minWidth: 160,
    flex: '1 1 160px',
  },
  heroMetricLabel: {
    display: 'block',
    fontSize: fs(12),
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 6,
  },
  heroMetricValue: {
    fontSize: fs(18),
    fontWeight: 800,
  },
  heroRight: {
    display: 'grid',
    gridTemplateRows: '1fr 1fr',
    gap: 14,
  },
  heroRightMobile: {
    gridTemplateRows: 'auto',
  },
  heroGlassCard: {
    borderRadius: 22,
    padding: 18,
    background: 'rgba(255,255,255,0.14)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.14)',
  },
  heroGlassCardSecondary: {
    borderRadius: 22,
    padding: 18,
    background: 'rgba(255,255,255,0.09)',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  heroCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: 'rgba(255,255,255,0.82)',
    marginBottom: 18,
  },
  heroCardLabel: {
    fontSize: fs(13),
    fontWeight: 700,
  },
  heroCardAmount: {
    fontSize: fs(26),
    fontWeight: 900,
    marginBottom: 8,
  },
  heroCardMeta: {
    fontSize: fs(13),
    color: 'rgba(255,255,255,0.72)',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 16,
    marginBottom: 22,
  },
  statsGridMobile: {
    gridTemplateColumns: '1fr',
  },
  statCard: {
    background: 'rgba(255,255,255,0.88)',
    border: '1px solid rgba(15,23,42,0.06)',
    borderRadius: 22,
    padding: 18,
    boxShadow: '0 14px 40px rgba(15,23,42,0.05)',
  },
  statIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
  },
  statTitle: {
    color: '#64748b',
    fontSize: fs(13),
    fontWeight: 600,
  },
  statValue: {
    fontSize: fs(26),
    fontWeight: 800,
    margin: '8px 0 6px',
    color: '#0f172a',
    wordBreak: 'break-word',
  },
  statSub: {
    color: '#94a3b8',
    fontSize: fs(12.5),
    lineHeight: 1.5,
  },
  contentGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.65fr) minmax(320px, 0.95fr)',
    gap: 18,
    alignItems: 'start',
  },
  contentGridTablet: {
    gridTemplateColumns: '1fr',
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
    minWidth: 0,
  },
  card: {
    background: 'rgba(255,255,255,0.9)',
    border: '1px solid rgba(15,23,42,0.06)',
    borderRadius: 24,
    padding: 20,
    boxShadow: '0 20px 60px rgba(15,23,42,0.05)',
    minWidth: 0,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  cardHeaderSplit: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  cardTitle: {
    fontSize: fs(18),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 4,
  },
  cardSub: {
    fontSize: fs(13.5),
    color: '#64748b',
    lineHeight: 1.6,
  },
  quickActionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 18,
  },
  quickActionsGridMobile: {
    gridTemplateColumns: '1fr 1fr',
  },
  quickActionButton: {
    padding: 14,
    borderRadius: 18,
    border: '1px solid rgba(15,23,42,0.07)',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: fs(14),
    color: '#0f172a',
    minWidth: 0,
  },
  quickActionButtonActive: {
    background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)',
    border: '1px solid rgba(22,163,74,0.18)',
    boxShadow: '0 14px 30px rgba(34,197,94,0.10)',
  },
  quickActionIcon: {
    width: 38,
    height: 38,
    borderRadius: 12,
    background: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#334155',
    flexShrink: 0,
  },
  quickActionIconActive: {
    background: '#16a34a',
    color: '#fff',
  },
  actionPanel: {
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    borderRadius: 22,
    padding: 18,
  },
  actionPanelTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 18,
    flexWrap: 'wrap',
  },
  actionTitle: {
    fontSize: fs(17),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 6,
  },
  actionDescription: {
    fontSize: fs(13.5),
    color: '#64748b',
    lineHeight: 1.6,
  },
  actionDot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    marginTop: 6,
    flexShrink: 0,
  },
  actionForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  actionStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
  },
  label: {
    fontSize: fs(13),
    fontWeight: 700,
    color: '#334155',
  },
  inputField: {
    height: 48,
    borderRadius: 14,
    border: '1px solid rgba(15,23,42,0.10)',
    padding: '0 14px',
    fontSize: fs(14),
    outline: 'none',
    background: '#fff',
    width: '100%',
    minWidth: 0,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
  },
  formGridMobile: {
    gridTemplateColumns: '1fr',
  },
  miniInfoCard: {
    padding: 14,
    borderRadius: 16,
    background: '#fff',
    border: '1px solid rgba(15,23,42,0.07)',
  },
  miniInfoLabel: {
    display: 'block',
    fontSize: fs(12),
    color: '#64748b',
    marginBottom: 6,
  },
  miniInfoValue: {
    fontSize: fs(15),
    fontWeight: 800,
    color: '#0f172a',
    wordBreak: 'break-word',
  },
  primaryButton: {
    minHeight: 46,
    borderRadius: 14,
    border: 'none',
    background: 'linear-gradient(135deg, #16a34a, #166534)',
    color: '#fff',
    fontWeight: 800,
    fontSize: fs(14),
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(22,163,74,0.22)',
    padding: '12px 16px',
    width: '100%',
  },
  primaryButtonAlt: {
    minHeight: 46,
    borderRadius: 14,
    border: 'none',
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    color: '#fff',
    fontWeight: 800,
    fontSize: fs(14),
    cursor: 'pointer',
    boxShadow: '0 12px 26px rgba(245,158,11,0.22)',
    padding: '12px 16px',
    width: '100%',
  },
  secondaryButton: {
    minHeight: 44,
    padding: '10px 16px',
    borderRadius: 14,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    color: '#0f172a',
    fontWeight: 700,
    fontSize: fs(14),
    cursor: 'pointer',
    width: '100%',
  },
  loanEligibility: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#f0fdf4',
    color: '#166534',
    border: '1px solid rgba(22,163,74,0.16)',
    borderRadius: 14,
    padding: '12px 14px',
    fontSize: fs(13.5),
    fontWeight: 700,
    flexWrap: 'wrap',
  },
  statementCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '13px 14px',
    borderRadius: 14,
    background: '#eff6ff',
    color: '#1d4ed8',
    fontSize: fs(13.5),
    fontWeight: 700,
    flexWrap: 'wrap',
  },
  filterWrap: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '8px 12px',
    borderRadius: 999,
    border: '1px solid rgba(15,23,42,0.08)',
    background: '#fff',
    cursor: 'pointer',
    fontSize: fs(12.5),
    fontWeight: 700,
    color: '#475569',
    textTransform: 'capitalize',
  },
  filterButtonActive: {
    background: '#0f172a',
    color: '#fff',
    border: '1px solid #0f172a',
  },
  transactionSummary: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 14,
  },
  summaryPillGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 12px',
    borderRadius: 999,
    background: '#ecfdf5',
    color: '#166534',
    fontSize: fs(12.5),
    fontWeight: 800,
    flexWrap: 'wrap',
  },
  summaryPillOrange: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 12px',
    borderRadius: 999,
    background: '#fff7ed',
    color: '#c2410c',
    fontSize: fs(12.5),
    fontWeight: 800,
    flexWrap: 'wrap',
  },
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  mobileTransactionCard: {
    padding: 14,
    borderRadius: 18,
    border: '1px solid rgba(15,23,42,0.06)',
    background: '#fff',
  },
  mobileTransactionTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    marginBottom: 12,
  },
  mobileTransactionTitle: {
    fontSize: fs(14),
    fontWeight: 800,
    color: '#0f172a',
  },
  mobileTransactionMeta: {
    fontSize: fs(12.5),
    color: '#64748b',
    marginTop: 4,
  },
  mobileTransactionAmounts: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  mobileAmountBlock: {
    padding: 10,
    borderRadius: 14,
    background: '#f8fafc',
  },
  amountLabel: {
    display: 'block',
    fontSize: fs(11.5),
    color: '#64748b',
    marginBottom: 6,
  },
  tableWrap: {
    overflowX: 'auto',
    borderRadius: 18,
    border: '1px solid rgba(15,23,42,0.06)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 760,
    background: '#fff',
  },
  th: {
    textAlign: 'left',
    fontSize: fs(12.5),
    fontWeight: 800,
    color: '#64748b',
    padding: '14px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid rgba(15,23,42,0.06)',
  },
  tr: {
    borderBottom: '1px solid rgba(15,23,42,0.05)',
  },
  td: {
    padding: '14px 16px',
    fontSize: fs(13.5),
    color: '#475569',
  },
  tdStrong: {
    padding: '14px 16px',
    fontSize: fs(13.5),
    color: '#0f172a',
    fontWeight: 700,
  },
  tdDebit: {
    fontSize: fs(13.5),
    color: '#dc2626',
    fontWeight: 700,
  },
  tdCredit: {
    fontSize: fs(13.5),
    color: '#16a34a',
    fontWeight: 700,
  },
  categoryPill: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#f1f5f9',
    color: '#334155',
    fontSize: fs(11.5),
    fontWeight: 800,
    textTransform: 'capitalize',
  },
  statusPill: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: fs(11.5),
    fontWeight: 800,
  },
  statusPillSuccess: {
    background: '#ecfdf5',
    color: '#166534',
  },
  statusPillWarning: {
    background: '#fff7ed',
    color: '#c2410c',
  },
  emptyState: {
    textAlign: 'center',
    padding: 28,
    color: '#64748b',
    fontSize: fs(14),
  },
  investmentGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },
  investmentGridMobile: {
    gridTemplateColumns: '1fr',
  },
  investmentCard: {
    padding: 18,
    borderRadius: 20,
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    minWidth: 0,
  },
  investmentTag: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#dcfce7',
    color: '#166534',
    fontSize: fs(11.5),
    fontWeight: 800,
    marginBottom: 12,
  },
  investmentTitle: {
    fontSize: fs(16),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 8,
  },
  investmentMeta: {
    fontSize: fs(13.5),
    color: '#16a34a',
    fontWeight: 700,
    marginBottom: 6,
  },
  investmentMinimum: {
    fontSize: fs(12.5),
    color: '#64748b',
    marginBottom: 16,
  },
  goalAmount: {
    fontSize: fs(30),
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 8,
    wordBreak: 'break-word',
  },
  goalLabel: {
    color: '#64748b',
    fontSize: fs(13.5),
    marginBottom: 16,
  },
  progressTrack: {
    width: '100%',
    height: 10,
    borderRadius: 999,
    background: '#e2e8f0',
    overflow: 'hidden',
    marginBottom: 12,
  },
  progressFillGreen: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #16a34a, #22c55e)',
  },
  progressFillDark: {
    height: '100%',
    borderRadius: 999,
    background: 'linear-gradient(90deg, #0f172a, #16a34a)',
  },
  progressMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    fontSize: fs(12.5),
    color: '#64748b',
  },
  loanMetrics: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
  },
  metricRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: fs(13.5),
    color: '#475569',
    flexWrap: 'wrap',
  },
  dividendAmount: {
    fontSize: fs(28),
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 8,
  },
  dividendStatus: {
    display: 'inline-flex',
    width: 'fit-content',
    padding: '8px 12px',
    borderRadius: 999,
    background: '#f5f3ff',
    color: '#6d28d9',
    fontSize: fs(12.5),
    fontWeight: 800,
    marginBottom: 16,
  },
  inlineButtons: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 14,
  },
  announcementList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  announcementItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: 14,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
  },
  announcementIcon: {
    width: 36,
    height: 36,
    borderRadius: 12,
    background: '#ecfdf5',
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  announcementTitle: {
    fontSize: fs(14),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 4,
  },
  announcementText: {
    fontSize: fs(12.8),
    color: '#64748b',
    lineHeight: 1.6,
  },
  profileBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 16,
  },
  profileRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px dashed rgba(15,23,42,0.08)',
    fontSize: fs(13.5),
    color: '#475569',
    flexWrap: 'wrap',
  },
  walletGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },
  walletGridMobile: {
    gridTemplateColumns: '1fr',
  },
  walletCard: {
    padding: 18,
    borderRadius: 20,
    border: '1px solid rgba(15,23,42,0.07)',
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
  },
  walletCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 12,
  },
  walletTag: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#ecfdf5',
    color: '#166534',
    fontSize: fs(11.5),
    fontWeight: 800,
  },
  walletCardTitle: {
    fontSize: fs(16),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 8,
  },
  walletCardAmount: {
    fontSize: fs(24),
    fontWeight: 900,
    marginBottom: 8,
    color: '#0f172a',
  },
  walletCardText: {
    fontSize: fs(12.8),
    color: '#64748b',
    lineHeight: 1.6,
  },
  metricStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  metricCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 14,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
  },
  metricTitle: {
    fontSize: fs(12.5),
    color: '#64748b',
    marginBottom: 4,
  },
  metricValue: {
    fontSize: fs(15),
    fontWeight: 800,
    color: '#0f172a',
  },
  metricChip: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: 14,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
    fontSize: fs(13.5),
    color: '#475569',
    flexWrap: 'wrap',
  },
  loanFocusCard: {
    padding: 18,
    borderRadius: 20,
    background: 'linear-gradient(135deg, #effff4, #f8fafc)',
    border: '1px solid rgba(34,197,94,0.10)',
  },
  loanFocusTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    alignItems: 'center',
    marginBottom: 16,
  },
  loanFocusAmount: {
    fontSize: fs(30),
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 8,
  },
  loanFocusText: {
    fontSize: fs(13.5),
    color: '#64748b',
    marginBottom: 16,
  },
  scheduleList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  scheduleItem: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    alignItems: 'center',
    padding: 14,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
    flexWrap: 'wrap',
  },
  scheduleTitle: {
    fontSize: fs(14),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 4,
  },
  scheduleText: {
    fontSize: fs(12.8),
    color: '#64748b',
  },
  scheduleRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 8,
  },
  scheduleTag: {
    display: 'inline-flex',
    padding: '6px 10px',
    borderRadius: 999,
    background: '#eef2ff',
    color: '#4338ca',
    fontSize: fs(11.5),
    fontWeight: 800,
    width: 'fit-content',
  },
  loanProductMeta: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: fs(12.8),
    color: '#64748b',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  dividendFeatureCard: {
    padding: 18,
    borderRadius: 20,
    background: 'linear-gradient(135deg, #faf5ff, #ffffff)',
    border: '1px solid rgba(124,58,237,0.10)',
  },
  dividendFeatureAmount: {
    fontSize: fs(32),
    fontWeight: 900,
    color: '#0f172a',
    marginBottom: 10,
  },
  noteList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  noteItem: {
    padding: 14,
    borderRadius: 16,
    background: '#f8fafc',
    border: '1px solid rgba(15,23,42,0.05)',
    fontSize: fs(13.5),
    color: '#475569',
    lineHeight: 1.7,
  },
  securityGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 12,
  },
  securityCard: {
    padding: 18,
    borderRadius: 20,
    background: 'linear-gradient(180deg, #ffffff, #f8fafc)',
    border: '1px solid rgba(15,23,42,0.07)',
  },
  securityCardTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    marginBottom: 14,
  },
  securityIcon: {
    width: 40,
    height: 40,
    borderRadius: 14,
    background: '#ecfdf5',
    color: '#166534',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  securityIconSmall: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: '#eff6ff',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  securityTitle: {
    fontSize: fs(15),
    fontWeight: 800,
    color: '#0f172a',
    marginBottom: 8,
  },
  securityText: {
    fontSize: fs(12.8),
    color: '#64748b',
    lineHeight: 1.6,
  },
  scheduleWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
};

export default MemberDashboard;